import React, {
  useState,
  useEffect
} from "react";

import { Helmet } from "react-helmet";

import {
  FaSchool,
  FaUsers,
  FaChartLine,
  FaMobileAlt,
  FaCloud,
  FaArrowRight,
  FaCheckCircle,
  FaShieldAlt,
  FaDatabase,
  FaUserGraduate
} from "react-icons/fa";

const BengaluruSchoolManagement = () => {
  const [screenWidth, setScreenWidth] =
    useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () =>
      setScreenWidth(
        window.innerWidth
      );

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );
  }, []);

  const isMobile =
    screenWidth <= 768;

  const whatsappLink =
    "https://wa.me/919254147455?text=Hi%20I%20want%20demo%20for%20School%20Management%20Software%20in%20Bengaluru";

  const features = [
    {
      icon: <FaSchool />,
      title:
        "Complete School ERP",
      desc: "Advanced school management software for Bengaluru schools with complete automation."
    },

    {
      icon: <FaUsers />,
      title:
        "Student Management",
      desc: "Manage admissions, student records, attendance and academic performance digitally."
    },

    {
      icon: <FaChartLine />,
      title:
        "Smart Reports & Analytics",
      desc: "Generate school performance reports, attendance reports and fee reports instantly."
    },

    {
      icon: <FaMobileAlt />,
      title:
        "Mobile App for Schools",
      desc: "Teachers, parents and students can access school data from mobile devices."
    },

    {
      icon: <FaCloud />,
      title:
        "Cloud School Software",
      desc: "Secure cloud-based school management system with multi-device access."
    },

    {
      icon: <FaDatabase />,
      title:
        "School Database Management",
      desc: "Store student records, exams, fees and attendance securely online."
    }
  ];

  return (
    <div style={styles.page}>
      <Helmet>
        <title>
          Best School Management
          System in Bengaluru |
          School ERP Software
          Bengaluru | Gurukul ERP
        </title>

        <meta
          name="description"
          content="Best school management system in Bengaluru with smart school ERP software, attendance management, fee management, online admissions and cloud school automation."
        />

        <meta
          name="keywords"
          content="school management system Bengaluru, school ERP software Bengaluru, Bengaluru school management software, school software Bengaluru, best school ERP Bengaluru, school management app Bengaluru, cloud school software Bengaluru, school automation software Bengaluru"
        />

        <link
          rel="canonical"
          href="https://www.gurukulerp.com/bengaluru-school-management-system"
        />
      </Helmet>

      {/* HERO */}

      <section
        style={{
          ...styles.heroSection,
          flexDirection: isMobile
            ? "column"
            : "row"
        }}
      >
        <div style={styles.heroLeft}>
          <div style={styles.tag}>
            #1 School ERP Software
            in Bengaluru
          </div>

          <h1
            style={{
              ...styles.heading,
              textAlign: isMobile
                ? "center"
                : "left"
            }}
          >
            Best School ERP <br />

            <span
              style={{
                color: "#38bdf8"
              }}
            >
              Software in Bengaluru
            </span>
          </h1>

          <p
            style={{
              ...styles.subText,
              textAlign: isMobile
                ? "center"
                : "left"
            }}
          >
            Complete school
            management software for
            Bengaluru schools with
            attendance management,
            fee management, online
            admissions, exams,
            timetable management,
            mobile app and cloud ERP
            automation.
          </p>

          <div
            style={{
              ...styles.heroBtns,
              flexDirection: isMobile
                ? "column"
                : "row"
            }}
          >
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration:
                  "none",
                width: isMobile
                  ? "100%"
                  : "auto"
              }}
            >
              <button
                style={{
                  ...styles.primaryBtn,
                  width: isMobile
                    ? "100%"
                    : "auto"
                }}
              >
                Book Free Demo
              </button>
            </a>

            <button
              style={{
                ...styles.secondaryBtn,
                width: isMobile
                  ? "100%"
                  : "auto"
              }}
            >
              Explore Features
            </button>
          </div>

          <div
            style={{
              ...styles.trustGrid,
              gridTemplateColumns:
                isMobile
                  ? "1fr"
                  : "repeat(3,1fr)"
            }}
          >
            <div style={styles.trustCard}>
              <FaCheckCircle />

              <span>
                100% Cloud ERP
              </span>
            </div>

            <div style={styles.trustCard}>
              <FaShieldAlt />

              <span>
                Secure School Data
              </span>
            </div>

            <div style={styles.trustCard}>
              <FaUserGraduate />

              <span>
                Student Management
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT */}

        <div style={styles.heroRight}>
          <div style={styles.dashboardCard}>
            <div
              style={styles.dashboardTop}
            >
              <div>
                <h3>
                  Bengaluru School
                  Dashboard
                </h3>

                <span
                  style={{
                    color: "#94a3b8"
                  }}
                >
                  Live ERP Analytics
                </span>
              </div>

              <div style={styles.live}></div>
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.statBox}>
                <h2>2,450</h2>

                <p>Students</p>
              </div>

              <div style={styles.statBox}>
                <h2>96%</h2>

                <p>Attendance</p>
              </div>

              <div style={styles.statBox}>
                <h2>180+</h2>

                <p>Teachers</p>
              </div>

              <div style={styles.statBox}>
                <h2>24/7</h2>

                <p>Cloud Access</p>
              </div>
            </div>

            <div style={styles.chartWrap}>
              {[85, 95, 65, 88, 78].map(
                (item, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.chartBar,
                      height: `${item}%`
                    }}
                  ></div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}

      <section style={styles.featureSection}>
        <div style={styles.sectionTop}>
          <span style={styles.smallTag}>
            SCHOOL ERP FEATURES
          </span>

          <h2
            style={
              styles.sectionHeading
            }
          >
            Complete School
            Management Software for
            Bengaluru Schools
          </h2>

          <p style={styles.sectionSub}>
            Powerful school ERP
            software designed for
            schools and educational
            institutes in Bengaluru.
          </p>
        </div>

        <div
          style={{
            ...styles.featureGrid,
            gridTemplateColumns:
              isMobile
                ? "1fr"
                : "repeat(auto-fit,minmax(300px,1fr))"
          }}
        >
          {features.map(
            (item, index) => (
              <div
                key={index}
                style={
                  styles.featureCard
                }
              >
                <div
                  style={styles.iconBox}
                >
                  {item.icon}
                </div>

                <h3>{item.title}</h3>

                <p>{item.desc}</p>
              </div>
            )
          )}
        </div>
      </section>

      {/* WHY SECTION */}

      <section style={styles.whySection}>
        <div style={styles.whyTop}>
          <span
            style={styles.smallTag}
          >
            WHY GURUKUL ERP
          </span>

          <h2
            style={styles.whyHeading}
          >
            Why Bengaluru Schools
            Need Smart School
            Management Software
          </h2>

          <p style={styles.whySub}>
            Bengaluru is one of
            India’s fastest growing
            education hubs. Modern
            schools require advanced
            school ERP software to
            automate admissions,
            attendance,
            examinations, fee
            collection and parent
            communication.
          </p>
        </div>

        <div
          style={{
            ...styles.whyGrid,
            gridTemplateColumns:
              isMobile
                ? "1fr"
                : "1.1fr 1fr"
          }}
        >
          {/* LEFT */}

          <div
            style={styles.whyCardLarge}
          >
            <div
              style={styles.glow}
            ></div>

            <div
              style={styles.whyBadge}
            >
              Bengaluru School ERP
            </div>

            <h3
              style={
                styles.whyCardTitle
              }
            >
              Complete Digital
              School Management
              Platform
            </h3>

            <p
              style={
                styles.whyCardText
              }
            >
              Gurukul ERP helps
              Bengaluru schools
              automate operations,
              reduce paperwork and
              improve communication
              between teachers,
              parents and students
              through cloud-based
              school ERP software.
            </p>

            <div style={styles.statsWrap}>
              <div
                style={styles.statsMini}
              >
                <h4>500+</h4>

                <span>
                  Schools
                </span>
              </div>

              <div
                style={styles.statsMini}
              >
                <h4>50K+</h4>

                <span>
                  Students
                </span>
              </div>

              <div
                style={styles.statsMini}
              >
                <h4>99%</h4>

                <span>
                  Automation
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT */}

          <div
            style={styles.whyFeatures}
          >
            {[
              "Online School Admissions",
              "Smart Attendance Management",
              "Student Information System",
              "Online Fee Management",
              "Timetable Management",
              "School Mobile App",
              "Examination Management",
              
            ].map(
              (item, index) => (
                <div
                  key={index}
                  style={
                    styles.whyFeatureCard
                  }
                >
                  <div
                    style={styles.tick}
                  >
                    <FaCheckCircle />
                  </div>

                  <span>
                    {item}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* CTA */}

      <section style={styles.ctaSection}>
        <h2>
          Looking for School ERP
          Software in Bengaluru?
        </h2>

        <p>
          Start using the best
          school management system
          today.
        </p>

        <a
          href={whatsappLink}
          target="_blank"
          rel="noreferrer"
          style={styles.ctaBtn}
        >
          Get Free Demo

          <FaArrowRight />
        </a>
      </section>
    </div>
  );
};

const styles = {
  page: {
    background: "#020617",
    color: "#fff",
    fontFamily:
      "'Inter', sans-serif",
    overflow: "hidden"
  },

  heroSection: {
    display: "flex",
    alignItems: "center",
    justifyContent:
      "space-between",
    gap: "80px",
    padding: "120px 8%",
    minHeight: "100vh"
  },

  heroLeft: {
    flex: 1
  },

  heroRight: {
    flex: 1,
    display: "flex",
    justifyContent: "center"
  },

  tag: {
    display: "inline-block",
    background:
      "rgba(59,130,246,0.15)",
    border:
      "1px solid rgba(59,130,246,0.3)",
    padding: "10px 18px",
    borderRadius: "50px",
    color: "#60a5fa",
    marginBottom: "25px",
    fontWeight: "700"
  },

  heading: {
    fontWeight: "900",
    lineHeight: "1.1",
    marginBottom: "25px",
    fontSize:
      "clamp(42px,7vw,78px)"
  },

  subText: {
    color: "#94a3b8",
    fontSize: "18px",
    lineHeight: "1.9"
  },

  heroBtns: {
    display: "flex",
    gap: "18px",
    marginTop: "35px"
  },

  primaryBtn: {
    background:
      "linear-gradient(90deg,#2563eb,#06b6d4)",
    color: "#fff",
    border: "none",
    padding: "18px 36px",
    borderRadius: "16px",
    fontWeight: "800",
    cursor: "pointer"
  },

  secondaryBtn: {
    background: "transparent",
    border:
      "1px solid rgba(255,255,255,0.2)",
    color: "#fff",
    padding: "18px 36px",
    borderRadius: "16px",
    fontWeight: "700",
    cursor: "pointer"
  },

  trustGrid: {
    display: "grid",
    gap: "18px",
    marginTop: "50px"
  },

  trustCard: {
    background:
      "rgba(255,255,255,0.05)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },

  dashboardCard: {
    width: "100%",
    maxWidth: "560px",
    background:
      "rgba(15,23,42,0.75)",
    backdropFilter: "blur(18px)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "32px",
    padding: "35px"
  },

  dashboardTop: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems: "center",
    marginBottom: "35px"
  },

  live: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    background: "#22c55e"
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(2,1fr)",
    gap: "18px"
  },

  statBox: {
    background:
      "rgba(255,255,255,0.05)",
    padding: "24px",
    borderRadius: "22px",
    textAlign: "center"
  },

  chartWrap: {
    height: "220px",
    display: "flex",
    alignItems: "flex-end",
    gap: "14px",
    marginTop: "35px"
  },

  chartBar: {
    flex: 1,
    borderRadius:
      "12px 12px 0 0",
    background:
      "linear-gradient(180deg,#38bdf8,#2563eb)"
  },

  featureSection: {
    padding: "100px 8%"
  },

  sectionTop: {
    textAlign: "center",
    marginBottom: "60px"
  },

  smallTag: {
    color: "#38bdf8",
    fontWeight: "700"
  },

  sectionHeading: {
    fontSize:
      "clamp(34px,5vw,58px)",
    fontWeight: "900",
    margin: "18px 0"
  },

  sectionSub: {
    color: "#94a3b8",
    maxWidth: "850px",
    margin: "0 auto",
    lineHeight: "1.9"
  },

  featureGrid: {
    display: "grid",
    gap: "30px"
  },

  featureCard: {
    background:
      "rgba(255,255,255,0.05)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "28px",
    padding: "35px"
  },

  iconBox: {
    width: "70px",
    height: "70px",
    borderRadius: "20px",
    background:
      "linear-gradient(135deg,#2563eb,#06b6d4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    marginBottom: "20px"
  },

  whySection: {
    padding: "120px 8%"
  },

  whyTop: {
    textAlign: "center",
    maxWidth: "900px",
    margin: "0 auto 70px"
  },

  whyHeading: {
    fontSize:
      "clamp(36px,5vw,62px)",
    fontWeight: "900",
    margin: "20px 0"
  },

  whySub: {
    color: "#94a3b8",
    lineHeight: "1.9",
    fontSize: "18px"
  },

  whyGrid: {
    display: "grid",
    gap: "35px"
  },

  whyCardLarge: {
    position: "relative",
    overflow: "hidden",
    background:
      "linear-gradient(135deg,rgba(37,99,235,0.18),rgba(8,145,178,0.12))",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "34px",
    padding: "45px"
  },

  glow: {
    position: "absolute",
    width: "240px",
    height: "240px",
    background:
      "rgba(56,189,248,0.15)",
    borderRadius: "50%",
    top: "-100px",
    right: "-100px",
    filter: "blur(30px)"
  },

  whyBadge: {
    display: "inline-block",
    background:
      "rgba(255,255,255,0.08)",
    padding: "10px 18px",
    borderRadius: "50px",
    color: "#38bdf8",
    marginBottom: "25px",
    fontWeight: "700"
  },

  whyCardTitle: {
    fontSize: "36px",
    lineHeight: "1.3",
    marginBottom: "20px"
  },

  whyCardText: {
    color: "#cbd5e1",
    lineHeight: "2",
    fontSize: "17px"
  },

  statsWrap: {
    display: "flex",
    gap: "20px",
    marginTop: "40px",
    flexWrap: "wrap"
  },

  statsMini: {
    background:
      "rgba(255,255,255,0.06)",
    padding: "20px 24px",
    borderRadius: "20px",
    minWidth: "120px",
    textAlign: "center"
  },

  whyFeatures: {
    display: "grid",
    gap: "18px"
  },

  whyFeatureCard: {
    display: "flex",
    alignItems: "center",
    gap: "18px",
    background:
      "rgba(255,255,255,0.05)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    padding: "24px",
    borderRadius: "24px"
  },

  tick: {
    width: "50px",
    height: "50px",
    borderRadius: "16px",
    background:
      "linear-gradient(135deg,#2563eb,#06b6d4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },

  ctaSection: {
    margin: "0 8% 80px",
    padding: "100px 30px",
    borderRadius: "40px",
    textAlign: "center",
    background:
      "linear-gradient(135deg,#2563eb,#06b6d4)"
  },

  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "30px",
    background: "#fff",
    color: "#0f172a",
    textDecoration: "none",
    padding: "18px 34px",
    borderRadius: "14px",
    fontWeight: "800"
  },

  mobileSticky: {
    position: "fixed",
    bottom: "0",
    left: "0",
    width: "100%",
    padding: "12px",
    background: "#020617",
    zIndex: "999"
  },

  mobileBtn: {
    display: "block",
    width: "100%",
    textAlign: "center",
    background:
      "linear-gradient(90deg,#2563eb,#06b6d4)",
    color: "#fff",
    padding: "16px",
    borderRadius: "14px",
    textDecoration: "none",
    fontWeight: "800"
  }
};

export default BengaluruSchoolManagement;